import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import type { ConformityV2Framework } from "../../../api/conformityApi"

export const MIN_REASON = 10

/** Q2 override confirmation (design): reason ≥ 10 chars, Annex 3 warning,
 *  the affected components listed. The caller owns the mutation. */
export function OverrideDialog({
  framework,
  onConfirm,
  onClose,
  pending,
}: {
  framework: ConformityV2Framework
  onConfirm: (reason: string) => void
  onClose: () => void
  pending: boolean
}) {
  const { translate } = useTranslation()
  const [reason, setReason] = useState("")
  const valid = reason.trim().length >= MIN_REASON

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate("MC_CONF_V2_OVERRIDE_TITLE", { name: framework.label })}
          </DialogTitle>
          <p className="text-[12.5px] leading-relaxed text-grey5">
            {translate("MC_CONF_V2_OVERRIDE_BODY", {
              name: framework.label,
              n: framework.gaps.length,
            })}
          </p>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          {framework.gaps.map((gap) => (
            <div
              key={gap.articleId}
              className="flex items-center gap-2.5 rounded-md border border-white/[0.07] px-2.5 py-2 text-[12px]"
            >
              <span className="font-mono text-white">
                {gap.articleNumber ?? gap.articleId.slice(0, 8)}
              </span>
              <span className="flex-1" />
              <span className="text-grey5">{gap.supplierName}</span>
            </div>
          ))}
        </div>
        {framework.allComponentsUncovered && (
          // Decision 2026-09-17: the override is allowed with nothing covered,
          // so the weight of that decision is stated here, right above the
          // reason the user has to give for it.
          <p className="rounded-md border border-warning/60 px-3 py-2 text-[12px] leading-relaxed text-warning">
            {translate("MC_CONF_V2_OVERRIDE_NO_DATA_WARNING")}
          </p>
        )}
        <div>
          <label
            htmlFor="override-reason"
            className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-widest text-grey5"
          >
            {translate("MC_CONF_V2_OVERRIDE_REASON")}
          </label>
          <textarea
            id="override-reason"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={translate("MC_CONF_V2_OVERRIDE_PLACEHOLDER")}
            className="w-full resize-y rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] text-white outline-hidden focus:border-yellow1"
          />
          <div
            className={cn(
              "mt-1 flex items-center justify-between text-[11px]",
              valid ? "text-grey5" : "text-warning",
            )}
          >
            <span>
              {valid
                ? translate("MC_CONF_V2_OVERRIDE_LOGGED")
                : translate("MC_CONF_V2_OVERRIDE_MIN")}
            </span>
            <span className="font-mono">
              {reason.trim().length} / {MIN_REASON}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            onClick={onClose}
          >
            {translate("MC_CS_CONF_EDIT_CANCEL")}
          </Button>
          <Button
            className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
            disabled={!valid || pending}
            onClick={() => onConfirm(reason.trim())}
          >
            {pending && <Loader2 size={13} className="mr-1.5 animate-spin" />}
            {translate("MC_CONF_V2_OVERRIDE_CONFIRM")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
